import React, { useState } from "react";
import "./Modal.css";
import CustomEditor from "../../../../components/custom-textEditor/CustomEditor";
import Button from "../../../../components/reusable-button/Button";
import ReusableLoader from "../../../../components/reusable-loader/ReusableLoader";
import { RxCross2 } from "react-icons/rx";

const CrossIcon = RxCross2 as unknown as React.FC<
  React.SVGProps<SVGSVGElement>
>;

interface FileImage {
  src: string;
  maskName: string;
}

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  modalType: "image" | "reject" | "approve" | null;
  loading?: boolean;
  modalImages?: FileImage[];
  rejectReason?: string;
  setRejectReason?: (value: string) => void;
  handleApprove?: () => void;
  confirmReject?: () => void;
  setApproveMailBody?: (value: string) => void;
  approveMailBody?: string;
  btnLoading?: boolean;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  modalType,
  loading = false,
  modalImages = [],
  rejectReason = "",
  setRejectReason = () => {},
  handleApprove = () => {},
  confirmReject = () => {},
  setApproveMailBody = () => {},
  approveMailBody,
  btnLoading = false,
}) => {
  const [error, setError] = useState<string>("");

  if (!isOpen || !modalType) return null;

  const handleAction = () => {
    if (modalType === "approve") {
      if (!approveMailBody || approveMailBody.trim() === "") {
        setError("Email body is required");
        return;
      }
      setError("");
      handleApprove();
    } else if (modalType === "reject") {
      if (!rejectReason || rejectReason.trim() === "") {
        setError("Rejection reason is required");
        return;
      }
      setError("");
      confirmReject();
    }
  };

  return (
    <div className="modal-overlay">
      <div
        className="voucher-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            fontSize: "20px",
            fontWeight: "bold",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <h3 style={{ margin: 0 }}>
            {modalType === "reject"
              ? "Reject Request"
              : modalType === "approve"
              ? "Approve Request"
              : "View file"}
          </h3>
          <CrossIcon
            onClick={() => {
              onClose();
              setError("");
            }}
            style={{ cursor: "pointer" }}
          />
        </div>

        {modalType === "image" ? (
          loading ? (
            <p>
              <ReusableLoader />
            </p>
          ) : (
            <div className="file-name-list">
              {modalImages.map((file, i) => {
                const isPdf = file.maskName.toLowerCase().endsWith(".pdf");
                return (
                  <div key={i} style={{ marginBottom: "20px" }}>
                    {isPdf ? (
                      <iframe
                        src={file.src}
                        title={`PDF ${i}`}
                        width="100%"
                        height="500px"
                        style={{
                          border: "1px solid #ccc",
                          borderRadius: "6px",
                        }}
                      />
                    ) : (
                      <img
                        src={file.src}
                        alt={file.maskName}
                        style={{
                          cursor: "pointer",
                          maxWidth: "100%",
                          maxHeight: "400px",
                          borderRadius: "6px",
                          display: "block",
                          marginBottom: "8px",
                        }}
                      />
                    )}
                    <p>{file.maskName}</p>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          <>
            <p style={{ marginBottom: 8 }}>
              {modalType === "approve" ? "Email body" : "Rejection Reason"}
            </p>
            {modalType === "approve" ? (
              <CustomEditor
                value={approveMailBody ?? ""}
                onChange={(value) => {
                  setApproveMailBody(value);
                  setError(""); // clear error when typing
                }}
                file="voucher-request"
              />
            ) : (
              <textarea
                id="reason"
                value={rejectReason}
                onChange={(e) => {
                  setRejectReason(e.target.value);
                  setError(""); // clear error when typing
                }}
                placeholder="Enter rejection reason"
                rows={4}
                style={{ width: "100%" }}
              />
            )}

            {error && (
              <p style={{ color: "red", fontSize: "14px", marginTop: "4px" }}>
                {error}
              </p>
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
                marginTop: "16px",
              }}
            >
              <Button
                onClick={() => {
                  onClose();
                  setError("");
                }}
                label="Cancel"
                variant="outlined"
              />
              <Button
                onClick={handleAction}
                label={modalType === "approve" ? "Approve" : "Reject"}
                loading={btnLoading}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Modal;
